import { useState } from 'react'
import { Card, Row, Col, Table, Badge, Form, Spinner, Alert, ProgressBar } from 'react-bootstrap'
import { useQuery } from 'react-query'
import api from '../../services/api'
import { useSyncedLocalClientId } from '../../hooks/useSyncedLocalClientId'
import { getCurrentYearMx } from '../../utils/apiDateFormat'
import { formatCurrency } from '../../utils/formatters'

const BudgetDashboard = () => {
  const { clientId: clienteId, setClientId: setClienteId, selectDisabled } = useSyncedLocalClientId()
  const [año, setAño] = useState(getCurrentYearMx().toString())

  // Obtener lista de clientes para el filtro
  const { data: clientesData } = useQuery(
    'clients',
    async () => {
      const response = await api.get('/clients/clients/?activo=true')
      return response.data.results || response.data || []
    }
  )
  
  // Asegurar que clientes sea siempre un array
  const clientes = Array.isArray(clientesData) ? clientesData : []

  // Obtener datos del dashboard
  const { data: dashboardData, isLoading, error } = useQuery(
    ['budget-dashboard', clienteId, año],
    async () => {
      const params = new URLSearchParams()
      if (clienteId) params.append('cliente_id', clienteId)
      if (año) params.append('año', año)
      const response = await api.get(`/financial/budgets/dashboard/?${params.toString()}`)
      return response.data
    }
  )

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Cargando dashboard...</p>
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="danger">
        Error al cargar dashboard: {error.message}
      </Alert>
    )
  }

  if (!dashboardData) {
    return (
      <Alert variant="info">
        No hay datos disponibles para el dashboard.
      </Alert>
    )
  }

  const { kpis, presupuestos_con_alertas, top_presupuestos } = dashboardData

  return (
    <div>
      {/* Filtros */}
      <Card className="mb-3">
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Cliente</Form.Label>
                <Form.Select
                  value={clienteId}
                  onChange={(e) => setClienteId(e.target.value)}
                  disabled={selectDisabled}
                >
                  <option value="">Todos los clientes</option>
                  {Array.isArray(clientes) && clientes.map((cliente) => (
                    <option key={cliente.id} value={cliente.id}>
                      {cliente.razon_social || cliente.business_name || `${cliente.nombre} ${cliente.apellido_paterno || ''}`}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Año</Form.Label>
                <Form.Control
                  type="number"
                  value={año}
                  onChange={(e) => setAño(e.target.value)}
                  min="2020"
                  max="2030"
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* KPIs */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h6 className="text-muted mb-2">Total Presupuestado</h6>
              <h3 className="mb-0">
                {formatCurrency(kpis.total_presupuestado || 0)}
              </h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h6 className="text-muted mb-2">Total Ejecutado</h6>
              <h3 className="mb-0 text-success">
                {formatCurrency(kpis.total_ejecutado || 0)}
              </h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h6 className="text-muted mb-2">Total Comprometido</h6>
              <h3 className="mb-0 text-warning">
                {formatCurrency(kpis.total_comprometido || 0)}
              </h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h6 className="text-muted mb-2">% Ejecución</h6>
              <h3 className="mb-0">
                {parseFloat(kpis.porcentaje_ejecucion || 0).toFixed(2)}%
              </h3>
              <ProgressBar
                variant={kpis.porcentaje_ejecucion > 100 ? 'danger' : kpis.porcentaje_ejecucion > 80 ? 'warning' : 'success'}
                now={Math.min(kpis.porcentaje_ejecucion || 0, 100)}
                style={{ height: '8px', marginTop: '10px' }}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Estadísticas Adicionales */}
      <Row className="mb-4">
        <Col md={4}>
          <Card>
            <Card.Body>
              <h6 className="text-muted mb-2">Total Presupuestos</h6>
              <h2 className="mb-0">{kpis.total_presupuestos || 0}</h2>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card>
            <Card.Body>
              <h6 className="text-muted mb-2">Sobre Ejecutados</h6>
              <h2 className="mb-0 text-danger">{kpis.presupuestos_sobre_ejecutados || 0}</h2>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card>
            <Card.Body>
              <h6 className="text-muted mb-2">En Riesgo (80-100%)</h6>
              <h2 className="mb-0 text-warning">{kpis.presupuestos_en_riesgo || 0}</h2>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Presupuestos con Alertas */}
      {presupuestos_con_alertas && presupuestos_con_alertas.length > 0 && (
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">Presupuestos con Alertas</h5>
          </Card.Header>
          <Card.Body>
            <Table responsive striped hover>
              <thead>
                <tr>
                  <th>Presupuesto</th>
                  <th>Cliente</th>
                  <th>% Ejecución</th>
                  <th>Alertas Críticas</th>
                  <th>Alertas Altas</th>
                  <th>Total Alertas</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {presupuestos_con_alertas.map((presupuesto) => (
                  <tr key={presupuesto.id}>
                    <td>{presupuesto.nombre}</td>
                    <td>{presupuesto.cliente_name || 'N/A'}</td>
                    <td>
                      <ProgressBar
                        variant={presupuesto.porcentaje_ejecucion > 100 ? 'danger' : presupuesto.porcentaje_ejecucion > 80 ? 'warning' : 'success'}
                        now={Math.min(presupuesto.porcentaje_ejecucion, 100)}
                        label={`${presupuesto.porcentaje_ejecucion.toFixed(1)}%`}
                        style={{ width: '100px' }}
                      />
                    </td>
                    <td>
                      <Badge bg="danger">{presupuesto.alertas_criticas}</Badge>
                    </td>
                    <td>
                      <Badge bg="warning">{presupuesto.alertas_altas}</Badge>
                    </td>
                    <td>
                      <Badge bg="secondary">{presupuesto.alertas_count}</Badge>
                    </td>
                    <td>
                      <Badge bg="info" style={{ cursor: 'pointer' }} title="Ver en la pestaña de Presupuestos">
                        ID: {presupuesto.id}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {/* Top 5 Presupuestos */}
      {top_presupuestos && top_presupuestos.length > 0 && (
        <Card>
          <Card.Header>
            <h5 className="mb-0">Top 5 Presupuestos por Monto Ejecutado</h5>
          </Card.Header>
          <Card.Body>
            <Table responsive striped hover>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Presupuesto</th>
                  <th>Cliente</th>
                  <th>Monto Ejecutado</th>
                  <th>% Ejecución</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {top_presupuestos.map((presupuesto, index) => (
                  <tr key={presupuesto.id}>
                    <td>{index + 1}</td>
                    <td>{presupuesto.nombre}</td>
                    <td>{presupuesto.cliente_name || 'N/A'}</td>
                    <td>
                      {formatCurrency(presupuesto.monto_ejecutado || 0)}
                    </td>
                    <td>
                      <ProgressBar
                        variant={presupuesto.porcentaje_ejecucion > 100 ? 'danger' : presupuesto.porcentaje_ejecucion > 80 ? 'warning' : 'success'}
                        now={Math.min(presupuesto.porcentaje_ejecucion, 100)}
                        label={`${presupuesto.porcentaje_ejecucion.toFixed(1)}%`}
                        style={{ width: '100px' }}
                      />
                    </td>
                    <td>
                      <Badge bg="info" style={{ cursor: 'pointer' }} title="Ver en la pestaña de Presupuestos">
                        ID: {presupuesto.id}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {(!presupuestos_con_alertas || presupuestos_con_alertas.length === 0) && 
       (!top_presupuestos || top_presupuestos.length === 0) && (
        <Alert variant="info">
          No hay presupuestos vigentes para mostrar en el dashboard.
        </Alert>
      )}
    </div>
  )
}

export default BudgetDashboard
